import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button, Input } from '../../components/ui'
import { useAuth } from '../../context/AuthContext'
import { useModal } from '../../context/ModalContext'
import { authApi } from '../../lib/api/auth'
import { fileToSquareAvatar } from '../../lib/avatar'
import { ApiRequestError } from '../../lib/api/client'

const schema = z.object({ name: z.string().trim().min(1, 'El nombre es obligatorio') })
type FormValues = z.infer<typeof schema>

export function ProfileModalContent({ onDone }: { onDone: () => void }) {
  const { session, setSession } = useAuth()
  const { alert } = useModal()
  const [avatarPreview, setAvatarPreview] = useState(session?.avatar ?? '')
  const [submitting, setSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { name: session?.name ?? '' } })

  async function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const dataUrl = await fileToSquareAvatar(file)
    setAvatarPreview(dataUrl)
  }

  async function onSubmit(values: FormValues) {
    if (!session) return
    setSubmitting(true)
    try {
      const payload: { name: string; avatar?: string } = { name: values.name }
      if (avatarPreview && avatarPreview !== session.avatar) payload.avatar = avatarPreview
      const updated = await authApi.updateProfile(payload)
      setSession({ ...session, name: updated.name, avatar: updated.avatar ?? '' })
      onDone()
      await alert('Perfil', 'Perfil actualizado')
    } catch (err) {
      await alert('No se pudo guardar', err instanceof ApiRequestError ? err.message : 'Error inesperado')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-panel-2">
          {avatarPreview ? (
            <img src={avatarPreview} alt="Vista previa del avatar" className="h-full w-full object-cover" />
          ) : (
            <span className="text-muted">—</span>
          )}
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <Input label="Nombre" {...register('name')} error={errors.name?.message} />
          <input type="file" accept="image/*" onChange={onFileChange} className="text-sm text-muted" />
        </div>
      </div>
      <div className="mt-2 flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancelar
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Guardando…' : 'Guardar'}
        </Button>
      </div>
    </form>
  )
}
